import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui";
import { PublicShell } from "@/public/PublicShell";

type Pay = { id: string; kind: string; amount: string; status: string; checkout_url: string | null; manage_token: string | null; appointment_status: string | null; pending?: boolean; viva_status?: string };

/** /pay/:paymentId — Viva sends the customer back here with ?t=<transaction id>; we ask our server to verify. */
export function PayReturn() {
  const { paymentId = "" } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [pay, setPay] = useState<Pay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const failed = params.get("failed") === "1";
  const trx = params.get("t");
  useEffect(() => {
    let tries = 0;
    const run = async () => {
      try {
        const p = trx && !failed ? await api<Pay>(`/public/payments/${paymentId}/verify`, { method: "POST", body: JSON.stringify({ transaction_id: trx }) }) : await api<Pay>(`/public/payments/${paymentId}`);
        setPay(p);
        if (p.status === "pending" && p.pending && tries++ < 6) setTimeout(run, 3000);
      } catch (e) { setError(e instanceof ApiError ? e.detail : t("app.error")); }
    };
    run();
  }, [paymentId, trx, failed, t]);
  return (
    <PublicShell title={t("pay.title")}>
      <section className="rounded-2xl bg-white shadow p-5 space-y-3 text-sm">
        {error && <p className="text-red-700">{error}</p>}
        {!pay && !error && <p className="text-gray-500">{t("pay.checking")}</p>}
        {pay?.status === "paid" && (
          <div className="space-y-2">
            <h2 className="font-semibold text-green-700">{t("pay.paid", { amount: pay.amount })}</h2>
            <p>{pay.kind === "deposit" ? t("pay.depositDone") : t("pay.packageDone")}</p>
            <div className="flex gap-2">
              {pay.manage_token && <a className="rounded-lg border px-3 py-1.5" href={`/b/${pay.manage_token}`}>{t("book.manage")}</a>}
              {pay.kind === "package" && <a className="rounded-lg border px-3 py-1.5" href="/me">{t("pay.myAccount")}</a>}
            </div>
          </div>
        )}
        {pay && pay.status === "pending" && (
          <div className="space-y-2">
            <p className="text-amber-700">{failed ? t("pay.failed") : pay.pending ? t("pay.stillPending") : t("pay.notPaid")}</p>
            {pay.checkout_url && <Button type="button" onClick={() => { location.href = pay.checkout_url!; }}>{t("pay.retry")}</Button>}
            {pay.kind === "deposit" && <p className="text-xs text-gray-500">{t("pay.holdHint")}</p>}
          </div>
        )}
        {pay && pay.status === "failed" && <p className="text-red-700">{t("pay.expired")}</p>}
        {pay && pay.status === "refunded" && <p className="text-gray-700">{t("pay.refunded")}</p>}
      </section>
    </PublicShell>
  );
}
